import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  BriefcaseBusiness,
  Check,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Menu,
  Network,
  PanelLeftClose,
  Play,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./NavigationTour.scss";

const STORAGE_KEY = "navigation-tour-v1-seen";
const SCENE_DURATION = 3200;

const scenes = [
  {
    eyebrow: "A tidier workspace",
    title: "Your sidebar, now compact",
    description:
      "Keep the company brand and your main work areas within easy reach. Collapse the sidebar when you want more room.",
  },
  {
    eyebrow: "Find a module faster",
    title: "Main menus live in the navbar",
    description:
      "Choose a module such as ESS or Teams from the top navigation. The sidebar stays focused on your workspace.",
  },
  {
    eyebrow: "Keep related pages together",
    title: "Page links follow the module",
    description:
      "When a module has submenus, its pages appear in a second row so you can switch without hunting through menus.",
  },
];

const markSeen = () => {
  try {
    window.localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // The tour still closes when browser storage is unavailable.
  }
};

const NavigationPreview = ({ scene, companyName, companyLogo }) => (
  <div className={`navigation-tour-preview scene-${scene}`} aria-hidden="true">
    <div className="tour-preview-sidebar">
      <div className="tour-preview-brand">
        {companyLogo ? <img src={companyLogo} alt="" /> : <span>O</span>}
        <b>{companyName}</b>
      </div>
      <div className="tour-preview-side-item">
        <LayoutGrid />
        <span>Home</span>
      </div>
      <div className="tour-preview-side-item is-active">
        <Users />
        <span>ESS</span>
      </div>
      <div className="tour-preview-side-item">
        <BriefcaseBusiness />
        <span>Teams</span>
      </div>
      <div className="tour-preview-collapse">
        <PanelLeftClose />
        <span>Collapse</span>
      </div>
    </div>
    <div className="tour-preview-main">
      <div className="tour-preview-topbar">
        <div className="tour-preview-menu">
          <Menu />
          <span>Overview</span>
        </div>
        <div className="tour-preview-links">
          <span className={scene === 1 ? "selected" : ""}>
            <Users /> ESS
          </span>
          <span className={scene === 2 ? "selected" : ""}>
            <Network /> Teams
          </span>
          <span>
            <BarChart3 /> Analytics
          </span>
        </div>
        <div className="tour-preview-avatar">M</div>
      </div>
      <div className={`tour-preview-subnav${scene === 2 ? " is-visible" : ""}`}>
        <span className="subnav-current">
          {scene === 2 ? "Manage leave" : "My leave"}
        </span>
        <span>{scene === 2 ? "Team attendance" : "Attendance"}</span>
        <span>{scene === 2 ? "Time sheets" : "My time sheet"}</span>
      </div>
      <div className="tour-preview-content">
        <div className="tour-preview-welcome">
          <span>Good morning</span>
          <i />
        </div>
        <div className="tour-preview-cards">
          <i />
          <i />
          <i />
        </div>
        <div className="tour-preview-table">
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
    </div>
  </div>
);

const NavigationTour = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(true);
  const companyName = user?.companyName || "HRLabs";
  const companyLogo =
    user?.logo && user?.logoType
      ? `data:${user.logoType};base64,${user.logo}`
      : null;

  useEffect(() => {
    try {
      if (!window.localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  const finish = useCallback(() => {
    markSeen();
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open || !playing) return undefined;
    const timer = window.setTimeout(() => {
      if (scene === scenes.length - 1) finish();
      else setScene((current) => current + 1);
    }, SCENE_DURATION);
    return () => window.clearTimeout(timer);
  }, [finish, open, playing, scene]);

  const goTo = (nextScene) => {
    setScene(Math.max(0, Math.min(scenes.length - 1, nextScene)));
    setPlaying(false);
  };

  if (!open) return null;

  return (
    <div
      className="navigation-tour-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) finish();
      }}
    >
      <section
        className="navigation-tour"
        role="dialog"
        aria-modal="true"
        aria-labelledby="navigation-tour-title"
        aria-describedby="navigation-tour-description"
      >
        <button
          className="navigation-tour-close"
          type="button"
          onClick={finish}
          aria-label="Close tutorial"
        >
          <X size={18} />
        </button>
        <div className="navigation-tour-copy">
          <span className="navigation-tour-kicker">
            <span className="tour-live-dot" /> A quick navigation tour
          </span>
          <h2 id="navigation-tour-title">{scenes[scene].title}</h2>
          <p id="navigation-tour-description">{scenes[scene].description}</p>
          <div className="navigation-tour-controls">
            <div
              className="navigation-tour-progress"
              aria-label={`Step ${scene + 1} of ${scenes.length}`}
            >
              {scenes.map((item, index) => (
                <button
                  type="button"
                  key={item.title}
                  className={
                    index === scene
                      ? "is-current"
                      : index < scene
                        ? "is-done"
                        : ""
                  }
                  onClick={() => goTo(index)}
                  aria-label={`Go to step ${index + 1}: ${item.title}`}
                />
              ))}
            </div>
            <span className="navigation-tour-step">
              0{scene + 1}
              <i> / 0{scenes.length}</i>
            </span>
          </div>
          <div className="navigation-tour-actions">
            <button
              type="button"
              className="tour-play-button"
              onClick={() => setPlaying((value) => !value)}
            >
              {playing ? (
                <span className="tour-pause-mark" aria-hidden="true">
                  Ⅱ
                </span>
              ) : (
                <Play size={15} fill="currentColor" />
              )}
              {playing ? "Pause" : "Play tour"}
            </button>
            {scene > 0 && (
              <button
                type="button"
                className="tour-back-button"
                onClick={() => goTo(scene - 1)}
                aria-label="Previous step"
              >
                <ChevronLeft size={18} />
              </button>
            )}
            {scene < scenes.length - 1 ? (
              <button
                type="button"
                className="tour-next-button"
                onClick={() => goTo(scene + 1)}
              >
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                className="tour-next-button"
                onClick={finish}
              >
                Got it <Check size={16} />
              </button>
            )}
          </div>
          <button
            type="button"
            className="navigation-tour-skip"
            onClick={finish}
          >
            Skip the tour
          </button>
        </div>
        <NavigationPreview
          scene={scene}
          companyName={companyName}
          companyLogo={companyLogo}
        />
      </section>
    </div>
  );
};

export default NavigationTour;
